import React, { useState } from "react";
import { Lock, Mail, Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import image from "../Image/image.jpg";
import API_BASE_URL from "../config";
import logo from "../Image/logo1.png";

export default function AdminLoginPage({ onLogin }) {
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [isLoading, setIsLoading] = useState(false);

   const logLoginAction = async (userData) => {
    try {
        await fetch(`${API_BASE_URL}api/Logs`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                action: "LOGIN",
                module: "Authentication",
                description: `User ${userData.username} logged in successfully`,
                userId: userData.userId,
                username: userData.username,
                fullName: userData.fullName || userData.username,
            })
        });
    } catch (err) {
        console.error("Failed to log login action:", err);
    }
};

const handleLogin = async () => {
    setIsLoading(true);

    if (!username || !password) {
        Swal.fire({
            icon: "warning",
            title: "Missing Information",
            text: "Please fill in all fields",
            confirmButtonColor: "#3b82f6"
        });
        setIsLoading(false);
        return;
    }

    try {
        const response = await fetch(`${API_BASE_URL}api/Users/login`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json"
            },
            body: JSON.stringify({ username, password })
        });

        const data = await response.json();

        if (!response.ok) {
            console.log("Login failed response:", data);

            if (data.isLicenseExpired) {
                await Swal.fire({
                    icon: "error",
                    title: "License Expired",
                    html: `<div style="text-align: left;">
                            <p>${data.message}</p>
                            <br>
                            <p><strong>Contact your administrator to renew your license.</strong></p>
                           </div>`,
                    confirmButtonColor: "#dc2626",
                    confirmButtonText: "OK"
                });
            } else {
                await Swal.fire({
                    icon: "error",
                    title: "Login Failed",
                    text: data.message || "Invalid username or password",
                    confirmButtonColor: "#dc2626"
                });
            }

        } else {
            console.log("=== LOGIN SUCCESSFUL ===");
            console.log("Full user data:", data);
            console.log("========================");

            // Save user data
            localStorage.setItem("user", JSON.stringify(data));
            sessionStorage.setItem("isLoggedIn", "true");

            // ✅ Log the login action
            await logLoginAction(data);

            // Show appropriate alert
            if (data.message && data.message.includes("Warning")) {
                await Swal.fire({
                    icon: "warning",
                    title: "License Expiring Soon",
                    html: `<div style="text-align: left;">
                            <p>${data.message}</p>
                            <br>
                            <p><strong>Please renew your license before it expires.</strong></p>
                           </div>`,
                    confirmButtonColor: "#f59e0b",
                    confirmButtonText: "Continue to Dashboard",
                    timer: 5000,
                    timerProgressBar: true
                });
            } else {
                await Swal.fire({
                    icon: "success",
                    title: "Login Successful",
                    text: "Welcome back!",
                    confirmButtonColor: "#10b981",
                    timer: 1500,
                    showConfirmButton: false
                });
            }

            // Redirect
            setTimeout(() => {
                if (onLogin && typeof onLogin === 'function') {
                    onLogin();
                } else {
                    navigate('/');
                }
            }, 500);
        }

    } catch (err) {
        console.error("Login error:", err);
        await Swal.fire({
            icon: "error",
            title: "Server Error",
            text: "Unable to connect to the server. Please try again later.",
            confirmButtonColor: "#dc2626"
        });
    } finally {
        setIsLoading(false);
    }
};

    const handleKeyPress = (e) => {
        if (e.key === "Enter") handleLogin();
    };

    return (
        <div
            className="min-h-screen flex items-center justify-center p-4 bg-cover bg-center"
            style={{ backgroundImage: `url(${image})` }}
        >
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400/20 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute bottom-20 right-10 w-96 h-96 bg-indigo-400/20 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-purple-400/20 rounded-full blur-3xl animate-pulse"></div>
            </div>

            {/* Login Card */}
            <div className="relative w-full max-w-md">
                <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl p-8 border border-white/20">
                    <div className="text-center mb-12">
                        <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-3xl mb-6 shadow-xl">
                            <div className="w-50 h-50 rounded-xl overflow-hidden shadow-lg ring-4 ring-white/40 bg-white">
                                <img src={logo} alt="Logo" className="w-full h-full object-cover" />
                            </div>
                        </div>
                        <h1 className="text-5xl font-bold text-slate-800 mb-4">Login</h1>
                        <p className="text-lg text-slate-600">Welcome back! Please enter your credentials</p>
                    </div>

                    <div className="space-y-5">
                        {/* Username Input */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">Username</label>
                            <div className="relative">
                                <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
                                <input
                                    type="text"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    placeholder="Enter username"
                                    className="w-full pl-12 pr-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white"
                                />
                            </div>
                        </div>

                        {/* Password Input */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">Password</label>
                            <div className="relative">
                                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
                                <input
                                    type={showPassword ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    placeholder="Enter your password"
                                    className="w-full pl-12 pr-12 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                                >
                                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>
                        </div>

                        {/* Login Button */}
                        <button
                            onClick={handleLogin}
                            disabled={isLoading}
                            className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 rounded-lg font-bold text-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] active:scale-[0.98]"
                        >
                            {isLoading ? (
                                <div className="flex items-center justify-center gap-2">
                                    <div className="w-5 h-5 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                                    <span>Signing in...</span>
                                </div>
                            ) : (
                                "Sign In"
                            )}
                        </button>
                    </div>
                    {/* Footer */}
                    <div className="mt-8 text-center">
                        <div className="flex items-center justify-center gap-2 mb-2">
                            <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center shadow-md">
                                <span className="text-white font-bold text-sm">QS</span>
                            </div>
                            <div className="text-left">
                                <p className="text-slate-800 font-bold text-sm leading-none">Powered By:</p>
                                <p className="text-blue-600 font-extrabold text-base leading-tight">QS I.T SERVICES</p>
                            </div>
                        </div>
                        <div className="flex items-center justify-center gap-2 text-slate-600">
                            <div className="h-px w-8 bg-gradient-to-r from-transparent to-slate-300"></div>
                            <span className="text-xs font-semibold">Version 1.0</span>
                            <div className="h-px w-8 bg-gradient-to-l from-transparent to-slate-300"></div>
                        </div>
                        <p className="text-slate-500 text-xs mt-2">© 2025 All rights reserved</p>
                    </div>
                </div>
            </div>
        </div>
    );
}